import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Honeypot from '../components/Honeypot';
import NavButtons from '../components/NavButtons';
import StepHeading from '../components/StepHeading';
import TextField from '../components/TextField';
import { BD } from '../bookDemoTheme';
import { isValidEmail } from '../bookDemoConstants';
import { BookDemoFormData } from '../types';

interface ContactStepProps {
  data: BookDemoFormData;
  onChange: (patch: Partial<BookDemoFormData>) => void;
  onBack: () => void;
  onContinue: () => void;
}

const ContactStep: React.FC<ContactStepProps> = ({ data, onChange, onBack, onContinue }) => {
  const { t } = useTranslation();
  const [emailTouched, setEmailTouched] = useState(false);

  const emailValid = isValidEmail(data.email);
  const canContinue = data.contactName.trim().length > 0 && emailValid && data.phone.trim().length > 0;

  return (
    <div className="flex flex-col flex-1">
      <StepHeading title={t('bookDemo.contact.title')} subtitle={t('bookDemo.contact.subtitle')} />

      <div
        className={`relative max-w-[606px] mx-auto w-full rounded-xl border p-4 sm:p-5 space-y-3 ${BD.panelBg} ${BD.borderStrong}`}
      >
        <TextField
          id="bookDemoContactName"
          label={t('bookDemo.contact.name.label')}
          value={data.contactName}
          onChange={value => onChange({ contactName: value })}
          placeholder={t('bookDemo.contact.name.placeholder')}
          autoComplete="name"
        />
        <TextField
          id="bookDemoEmail"
          type="email"
          label={t('bookDemo.contact.email.label')}
          value={data.email}
          onChange={value => onChange({ email: value })}
          onBlur={() => setEmailTouched(true)}
          placeholder={t('bookDemo.contact.email.placeholder')}
          autoComplete="email"
          error={emailTouched && data.email.trim().length > 0 && !emailValid ? t('bookDemo.contact.email.invalid') : undefined}
        />
        <TextField
          id="bookDemoPhone"
          type="tel"
          label={t('bookDemo.contact.phone.label')}
          value={data.phone}
          onChange={value => onChange({ phone: value })}
          placeholder={t('bookDemo.contact.phone.placeholder')}
          autoComplete="tel"
        />
        <Honeypot value={data.website} onChange={value => onChange({ website: value })} />
      </div>

      <NavButtons
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={t('bookDemo.contact.continue')}
        continueDisabled={!canContinue}
      />
    </div>
  );
};

export default ContactStep;
